import { isBatchNumber, normalizeBatchNumberInput } from "./batchNumber";

/**
 * Resolve a hardware-scanner payload to an in-app path.
 *
 * Work-order / sticker QRs may encode either a bare item id or a full
 * staff URL (`https://app…/batch-item/:id`, `/orders/:id`, …). The header
 * ITEM ID field historically always prefixed `/batch-item/`, which 404'd
 * when a full URL was pasted.
 *
 * Also accepts printed pull-sheet barcodes: `BATCH-{id}` → `/batches/:id/pull`.
 *
 * @param {string} raw
 * @param {{ handheld?: boolean, embroideryStation?: boolean, qcRegular?: boolean, qcStation?: boolean, neckLabelStation?: boolean }} [opts]
 * @returns {{ path: string, itemId: string|null, kind: "batch-item"|"order"|"order-item"|"batch-pull"|"batch-number"|"external-ref"|"tracking"|"unknown" }}
 */
const floorPath = (scan) =>
  `/handheld/floor?scan=${encodeURIComponent(scan)}`;

const stationPath = (itemId) =>
  `/embroidery-station/${encodeURIComponent(itemId)}`;

const qcStationPath = (itemId) =>
  `/qc-station/${encodeURIComponent(itemId)}`;

const neckLabelStationPath = (itemId) =>
  `/neck-label-station/${encodeURIComponent(itemId)}`;

const itemPath = (itemId, opts) => {
  // The fullscreen QC station stays on its own page. QC Regular still
  // uses the admin item page. Same barcode, the open screen picks it.
  if (opts.neckLabelStation) return neckLabelStationPath(itemId);
  if (opts.qcStation) return qcStationPath(itemId);
  if (opts.qcRegular) {
    return `/batch-item/${encodeURIComponent(itemId)}`;
  }
  if (opts.embroideryStation) return stationPath(itemId);
  if (opts.handheld) return floorPath(itemId);
  return `/batch-item/${encodeURIComponent(itemId)}`;
};

/**
 * ERP work ids and order numbers (WORK0353863, ORD2710082) printed on
 * customer paperwork: uppercase letters and digits, both present, 6 to 40
 * chars, no spaces. Riverr item labels carry dashes (51983-1-1, SS-9018),
 * batch doc ids are all digits, Firestore ids are mixed case and batch
 * numbers match BATCH_NUMBER_RE, so none of those land here. The batch page
 * asks the server to resolve the code through `externalRef`.
 */
export const EXTERNAL_REF_RE = /^(?=[A-Z0-9]*[A-Z])(?=[A-Z0-9]*[0-9])[A-Z0-9]{6,40}$/;

export const looksLikeExternalRef = (value) =>
  EXTERNAL_REF_RE.test(String(value || "").trim());

/**
 * Carrier tracking on a supplier box: UPS (1Z + 16) and USPS (20-22 digits
 * starting 91-95). Batch doc ids are 13 digits, so they never match.
 */
export const TRACKING_NUMBER_RE = /^(1Z[0-9A-Z]{16}|9[1-5]\d{18,20})$/;

export const looksLikeTrackingNumber = (value) =>
  TRACKING_NUMBER_RE.test(
    String(value || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "")
  );

export const resolveScannerDestination = (raw, opts = {}) => {
  const handheld = !!opts.handheld;
  const embroideryStation = !!opts.embroideryStation;
  const qcRegular = !!opts.qcRegular;
  const qcStation = !!opts.qcStation;
  const neckLabelStation = !!opts.neckLabelStation;
  const scanned = String(raw || "").trim();
  if (!scanned) {
    return { path: "", itemId: null, kind: "unknown" };
  }

  // Printed pull sheet Code128: BATCH-{id}
  const batchPull =
    scanned.match(/^BATCH[_-]?([A-Za-z0-9_-]+)$/i) ||
    scanned.match(/\/batches\/([A-Za-z0-9_-]+)(?:\/pull)?(?:\?|$)/i);
  if (batchPull && !/^https?:\/\//i.test(scanned) && !scanned.startsWith("/")) {
    const batchId = batchPull[1];
    return {
      path: handheld
        ? floorPath(scanned)
        : `/batches/${encodeURIComponent(batchId)}/pull?mode=handheld`,
      itemId: null,
      kind: "batch-pull",
    };
  }

  let pathname = scanned;

  // Absolute URL → use pathname (+ search is ignored for deep-links).
  if (/^https?:\/\//i.test(scanned)) {
    try {
      const u = new URL(scanned);
      pathname = u.pathname || "";
      const batchFromUrl = pathname.match(
        /^\/batches\/([^/]+)(?:\/pull)?$/i
      );
      if (batchFromUrl) {
        const batchId = decodeURIComponent(batchFromUrl[1]);
        return {
          path: handheld
            ? `/handheld/floor?batch=${encodeURIComponent(batchId)}`
            : `/batches/${encodeURIComponent(batchId)}/pull?mode=handheld`,
          itemId: null,
          kind: "batch-pull",
        };
      }
    } catch {
      // Fall through and treat as a bare id.
      pathname = scanned;
    }
  } else if (scanned.startsWith("/")) {
    pathname = scanned.split(/[?#]/)[0] || scanned;
  } else {
    // A supplier box's tracking label: receive that box.
    if (
      looksLikeTrackingNumber(scanned) &&
      !embroideryStation &&
      !qcStation &&
      !neckLabelStation
    ) {
      return {
        path: `/admin/inventory/receiving?scan=${encodeURIComponent(
          scanned
        )}`,
        itemId: scanned,
        kind: "tracking",
      };
    }
    // Human batch number (BSP-261007-014). The batch page falls back to the
    // item page when no batch has that number. Stations only scan items.
    if (
      isBatchNumber(scanned) &&
      !handheld &&
      !embroideryStation &&
      !qcRegular &&
      !qcStation &&
      !neckLabelStation
    ) {
      return {
        path: `/batches/${encodeURIComponent(normalizeBatchNumberInput(scanned))}`,
        itemId: null,
        kind: "batch-number",
      };
    }
    // ERP work id or order number. The batch page resolves it by
    // externalRef and shows the Needs labels screen when the order is held.
    if (
      looksLikeExternalRef(scanned) &&
      !handheld &&
      !embroideryStation &&
      !qcRegular &&
      !qcStation &&
      !neckLabelStation
    ) {
      return {
        path: `/batches/${encodeURIComponent(scanned)}`,
        itemId: null,
        kind: "external-ref",
      };
    }
    // Bare item id (or legacy order id pasted without a path).
    return {
      path: itemPath(scanned, {
        handheld,
        embroideryStation,
        qcRegular,
        qcStation,
        neckLabelStation,
      }),
      itemId: scanned,
      kind: "batch-item",
    };
  }

  // Normalize trailing slash.
  if (pathname.length > 1 && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }

  const batchPathMatch = pathname.match(/^\/batches\/([^/]+)(?:\/pull)?$/i);
  if (batchPathMatch) {
    const batchId = decodeURIComponent(batchPathMatch[1]);
    return {
      path: handheld
        ? `/handheld/floor?batch=${encodeURIComponent(batchId)}`
        : `/batches/${encodeURIComponent(batchId)}/pull?mode=handheld`,
      itemId: null,
      kind: "batch-pull",
    };
  }

  const orderItemMatch = pathname.match(
    /^\/orders\/([^/]+)\/items\/([^/]+)$/i
  );
  if (orderItemMatch) {
    const orderId = decodeURIComponent(orderItemMatch[1]);
    const itemId = decodeURIComponent(orderItemMatch[2]);
    return {
      path: `/orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(
        itemId
      )}`,
      itemId,
      kind: "order-item",
    };
  }

  const orderMatch = pathname.match(/^\/orders\/([^/]+)$/i);
  if (orderMatch) {
    const orderId = decodeURIComponent(orderMatch[1]);
    return {
      path: `/orders/${encodeURIComponent(orderId)}`,
      itemId: null,
      kind: "order",
    };
  }

  const batchItemMatch = pathname.match(/^\/batch-item\/([^/]+)$/i);
  if (batchItemMatch) {
    const itemId = decodeURIComponent(batchItemMatch[1]);
    return {
      path: itemPath(itemId, {
        handheld,
        embroideryStation,
        qcRegular,
        qcStation,
        neckLabelStation,
      }),
      itemId,
      kind: "batch-item",
    };
  }

  // Unknown path/URL — keep legacy behavior (treat whole string as item id).
  return {
    path: itemPath(scanned, {
      handheld,
      embroideryStation,
      qcRegular,
      qcStation,
      neckLabelStation,
    }),
    itemId: scanned,
    kind: "unknown",
  };
};
